import { Dispatch, SetStateAction, useRef } from 'react';
import Konva from 'konva';
import { RefObject } from 'react';
import { useSlideEditorContext } from 'providers/SlideEditorProvider';
import { SET_DELETE_MODAL } from 'reducers/SlideEditorReducer';
import { Text } from 'providers/SlideEditorProvider';

interface LocationText {
  x: number;
  y: number;
}

interface IProps {
  layerRef: RefObject<Konva.Layer>;
  setLocationTextNode: Dispatch<SetStateAction<LocationText>>;
  setShowModalStyle: Dispatch<SetStateAction<boolean>>;
  textsRef: React.MutableRefObject<Konva.Text[]>;
  transformerListRef: React.MutableRefObject<Konva.Transformer[]>;
  setTextActive: Dispatch<SetStateAction<number>>;
  setIsText: Dispatch<SetStateAction<boolean>>;
  setImageActive: Dispatch<SetStateAction<number | null>>;
}

const useTextEditor = ({
  layerRef,
  setLocationTextNode,
  setShowModalStyle,
  textsRef,
  transformerListRef,
  setTextActive,
  setIsText,
  setImageActive
}: IProps) => {
  const { slideEditorDispatch } = useSlideEditorContext();
  const textNodeRef = useRef<Konva.Text | null>(null);

  const hideAllTransformers = () => {
    transformerListRef.current.forEach(transformer => {
      transformer.hide();
    });
    layerRef.current?.draw();
  };

  const listener = (textNode: Konva.Text, transformer: Konva.Transformer) => {
    const activeLayer = layerRef.current;
    if (!activeLayer) return;

    textNode.on('click tap', () => {
      hideAllTransformers();
      setImageActive(null);
      setIsText(true);
      transformer.nodes([textNode]);
      transformer.show();
      transformer.moveToTop();
      setTextPosition(textNode);
      setTextActive(textsRef.current.indexOf(textNode));
      slideEditorDispatch({ type: SET_DELETE_MODAL, payload: true });
      activeLayer.draw();
    });

    textNode.on('transform', () => {
      const newWidth = textNode.width() * textNode.scaleX();
      textNode.width(newWidth);
      textNode.scaleX(1);
      activeLayer.batchDraw();
    });

    textNode.on('dblclick dbltap', () => {
      setTextActive(textsRef.current.indexOf(textNode));
      slideEditorDispatch({ type: SET_DELETE_MODAL, payload: false });
      setShowModalStyle(true);
      editTextNode(textNode, transformer, activeLayer);
    });

    textNode.on('dragstart', () => {
      setImageActive(null);
      setShowModalStyle(false);
      slideEditorDispatch({ type: SET_DELETE_MODAL, payload: false });
      hideAllTransformers();
    });

    textNode.on('dragmove', () => {
      setTextActive(textsRef.current.indexOf(textNode));
      setTextPosition(textNode);
    });
  };

  const addTextString = () => {
    const activeLayer = layerRef.current;
    if (!activeLayer) return;

    const textNode = new Konva.Text({
      wrap: 'word',
      text: 'Double click to edit text',
      x: 50,
      y: 80,
      fontSize: 20,
      draggable: true,
      width: 200,
      fill: 'white',
      fontStyle: 'normal'
    });

    textsRef.current.push(textNode);

    activeLayer.add(textNode);
    activeLayer.draw();

    const transformer = new Konva.Transformer({
      nodes: [textNode],
      enabledAnchors: ['middle-left', 'middle-right'],
      boundBoxFunc: (oldBox, newBox) => {
        newBox.width = Math.max(30, newBox.width);
        return newBox;
      }
    });

    listener(textNode, transformer);

    transformerListRef.current.push(transformer);

    activeLayer.add(transformer);
    transformer.attachTo(textNode);
    transformer.hide();
    activeLayer.draw();

    textNodeRef.current = textNode;
  };

  const setTextPosition = (textNode: Konva.Text) => {
    const textPosition = textNode.absolutePosition();
    const stage = textNode.getStage();
    const stageContainerRect = stage
      ? stage.container().getBoundingClientRect()
      : null;

    if (!stageContainerRect) return;

    const areaPosition = {
      x: stageContainerRect.left + textPosition.x,
      y: stageContainerRect.top + textPosition.y
    };
    setLocationTextNode({ x: areaPosition.x, y: areaPosition.y });
  };

  const editTextNode = (
    textNode: Konva.Text,
    transformer: Konva.Transformer,
    layer: Konva.Layer
  ) => {
    textNode.hide();
    transformer.hide();
    layer.draw();
    const rotation = textNode.rotation();

    const textPosition = textNode.absolutePosition();
    const stage = textNode.getStage();
    const stageContainerRect = stage
      ? stage.container().getBoundingClientRect()
      : null;

    if (!stageContainerRect) return;

    const areaPosition = {
      x: stageContainerRect.left + textPosition.x,
      y: stageContainerRect.top + textPosition.y
    };
    setLocationTextNode({ x: areaPosition.x, y: areaPosition.y });
    const textarea = document.createElement('textarea');
    document.body.appendChild(textarea);

    textarea.value = textNode.text();
    textarea.style.position = 'absolute';
    textarea.style.top = `${areaPosition.y}px`;
    textarea.style.left = `${areaPosition.x}px`;
    textarea.style.width = `${textNode.width() - textNode.padding() * 2}px`;
    textarea.style.height = `${
      textNode.height() - textNode.padding() * 2 + 5
    }px`;
    textarea.style.fontSize = `${textNode.fontSize()}px`;
    textarea.style.border = 'none';
    textarea.style.padding = '0px';
    textarea.style.margin = '0px';
    textarea.style.overflow = 'hidden';
    textarea.style.background = 'none';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.lineHeight = textNode.lineHeight().toString();
    textarea.style.fontFamily = textNode.fontFamily();
    textarea.style.transformOrigin = 'left top';
    textarea.style.textAlign = textNode.align();
    textarea.style.transform = `rotate(${rotation}deg)`;
    textarea.style.color = textNode.fill() as string;
    textarea.style.fontStyle = textNode.fontStyle();
    textarea.style.fontWeight = textNode.fontStyle();
    textarea.focus();

    const removeTextarea = () => {
      textarea.parentNode?.removeChild(textarea);
      window.removeEventListener('click', handleOutsideClick);
      textNode.show();
      layer.draw();
    };

    const setTextareaWidth = (newWidth?: number) => {
      if (!newWidth) {
        newWidth = textNode.text().length * textNode.fontSize();
      }
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );
      const isFirefox =
        navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
      if (isSafari || isFirefox) {
        newWidth = Math.ceil(newWidth);
      }
      const isEdge = /Edge/.test(navigator.userAgent);
      if (isEdge) {
        newWidth += 1;
      }
      textarea.style.width = `${newWidth}px`;
    };

    textarea.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        textNode.text(textarea.value);
        removeTextarea();
      }
      if (e.key === 'Escape') {
        removeTextarea();
      }
    });

    textarea.addEventListener('input', function () {
      const scale = textNode.getAbsoluteScale().x;
      setTextareaWidth(textNode.width() * scale);
      textarea.style.height = 'auto';
      textarea.style.height = `${
        textarea.scrollHeight + textNode.fontSize()
      }px`;
    });

    const handleOutsideClick = (e: MouseEvent) => {
      if (e.target !== textarea) {
        textNode.text(textarea.value);
        removeTextarea();
      }
    };

    setTimeout(() => {
      window.addEventListener('click', handleOutsideClick);
    });
  };

  const addTextOnSlide = async (textData: Text) => {
    return new Promise<void>(resolve => {
      const activeLayer = layerRef.current;
      if (!activeLayer) return;
      const textNode = new Konva.Text({
        text: textData.text,
        x: textData.x,
        y: textData.y,
        fontSize: textData.size,
        draggable: true,
        width: textData.width,
        fill: textData.color,
        fontStyle: textData.fontStyle,
        fontFamily: textData.font,
        align: textData.alignment,
        rotation: textData.roration
      });

      activeLayer.add(textNode);
      activeLayer.draw();

      const transformer = new Konva.Transformer({
        nodes: [textNode],
        enabledAnchors: ['middle-left', 'middle-right'],
        boundBoxFunc: (oldBox, newBox) => {
          newBox.width = Math.max(30, newBox.width);
          return newBox;
        }
      });

      listener(textNode, transformer);
      textsRef.current.push(textNode);
      transformerListRef.current.push(transformer);

      activeLayer.add(transformer);
      transformer.attachTo(textNode);
      transformer.hide();
      activeLayer.draw();

      textNodeRef.current = textNode;
      resolve();
    });
  };

  return { addTextString, addTextOnSlide };
};

export default useTextEditor;
